import { StatsCollector } from "../../stats/StatsCollector.js";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import { ClientSdkPublicInterface } from "../../sdk/ClientSdkPublicInterface.js";
import { CTAToStatsConverter } from "../stats/CTAToStatsConverter.js";
import { UserCTAEventNames, UserCTALogic } from "../UserCTALogic.js";
import { EvaluatedAction, SpecificEvaluatedAction, EvaluatedActionType, ManualAction } from "../EvaluatedAction.js";
import { CTAExecutionResult, EvaluatedActionExecutor, EvaluationName } from "./EvaluatedActionExecutor.js";
import { EvaluatedActionQueue, EvaluatedActionQueueElement, NamedCtaEvaluator } from "./EvaluatedActionExecutor.js";
import { LoginEvaluationExecutor } from "./executors/LoginEvaluationExecutor.js";
import { MessageEvaluationExecutor } from "./executors/MessageEvaluationExecutor.js";
import { AppDownloadEvaluationExecutor } from "./executors/AppDownloadEvaluationExecutor.js";
import { AppOpenEvaluationExecutor } from "./executors/AppOpenEvaluationExecutor.js";
import { PersonalDataCompletionEvaluationExecutor } from "./executors/PersonalDataCompletionEvaluationExecutor.js";
import { SubscriptionEvaluationExecutor } from "./executors/SubscriptionEvaluationExecutor.js";
import { inEnum } from "@ciaopeople/cp-shatsu/TypeChecking";
import { SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";

export abstract class DefaultCTAExecutor implements UserCTALogic, NamedCtaEvaluator {
    protected logger: Logger;
    protected executionCounter: number;

    private statsConverter: CTAToStatsConverter;

    private executors: EvaluatedActionExecutor[] = [];
    private queue: EvaluatedActionQueue = [];
    private events: SimpleEventHandler;

    constructor(protected readonly sdk: ClientSdkPublicInterface, 
        protected readonly stats: StatsCollector,
        protected readonly execOnce: boolean = true
    ) {
        this.logger = new BrowserLogger("[CTA]");
        this.executionCounter = 0;
        
        this.statsConverter = new CTAToStatsConverter();
        this.executors = [
            new LoginEvaluationExecutor( this.sdk, this.logger ),
            new MessageEvaluationExecutor( this.sdk, this.logger ),
            new AppDownloadEvaluationExecutor( this.sdk, this.logger ),
            new AppOpenEvaluationExecutor( this.sdk, this.logger ),
            new PersonalDataCompletionEvaluationExecutor( this.sdk, this.logger ),
            new SubscriptionEvaluationExecutor( this.sdk, this.logger )
        ];
        this.events = new SimpleEventHandler();
    }
    onUnderfollowShown(fun: () => void): void {
        this.events.on(UserCTAEventNames.UNDERFOLLOW_SHOWN, fun);
    }
    onCTAComplete(fun: () => void): void {
        this.events.on(UserCTAEventNames.UNDERFOLLOW_COMPLETE, fun);
    }
    onCTADismiss(fun: () => void): void {
        this.events.on(UserCTAEventNames.UNDERFOLLOW_DISMISS, fun);
    }

    private triggerUnderfollowShown(): void {
        this.events.trigger(UserCTAEventNames.UNDERFOLLOW_SHOWN, {});
    }


    signalNonUserDismiss() : void {
        this.triggerCTADismiss();
    }

    private triggerCTAComplete(): void {
        this.events.trigger(UserCTAEventNames.UNDERFOLLOW_COMPLETE, {});
    }
    private triggerCTADismiss(): void {
        this.events.trigger(UserCTAEventNames.UNDERFOLLOW_DISMISS, {});
    }

    public enqueueActionAndAwait(action: SpecificEvaluatedAction, data?: Record<string, any>): Promise<void> {
        this.enqueueAction(action, data);
        return Promise.resolve();
        
    }
    abstract evaluateCTA(): Promise<EvaluatedAction<any>|null>;

    /**
     * Esecuzione della coda completa, restituisce una promise che risolve a completamento dell'intera coda
     * @param element 
     */
    private processQueue(returnFirst: boolean = false): Promise<CTAExecutionResult> {
        const first = this.processFirstQueued();
        const complete = first.then(
            (exitus) => {
                if ( inEnum(exitus, CTAExecutionResult) ) {
                    if (exitus == CTAExecutionResult.ERROR) {
                        return Promise.resolve(exitus); // ? Sarebbe meglio un reject
                    }
                    else {
                        return this.processQueue();
                    }
                }
            }
        );
        return returnFirst ? first : complete;
    }
    
    private processFirstQueued(): Promise<CTAExecutionResult> {
        const elem = this.queue.shift();
        if (elem && elem.action) {
            if (elem.action.type == EvaluatedActionType.NO_FURTHER_EVALUATION) {
                this.logger.debug("No Further evaluation is required");
                this.flushQueue();
                return Promise.resolve(CTAExecutionResult.COMPLETED);
            }
            else {
                return this.processElement(elem);
            }
        }
        else {
            this.logger.debug("Queue is empty");
            return Promise.resolve(null);
        }
    }

    private flushQueue() {
        this.queue = [];
    }

    /**
     * Singolo step della coda, restituisce una promise che risolve ad ESECUZIONE dell'azione
     * @param element 
     */
    private processElement(element: EvaluatedActionQueueElement): Promise<CTAExecutionResult> {
        this.logger.debug("CTA to Execute", element.action);

        const executor = this.executors.filter(conv => conv.isExpectedType(element.action)).shift();

        const onMarkProm = element.action.onExecutionMark ? element.action.onExecutionMark() : Promise.resolve();

        return onMarkProm.then(
            () => {
                const shownData = this.statsConverter.ctaShown(element.action);
                if ( shownData!= null) {
                    this.stats.collectCTAData( shownData );
                }

                if (executor) {
                    window.setTimeout(() => this.triggerUnderfollowShown(), 0);
                    return executor.execute(this, element.action, element.data).then(
                        exitus => {
                            let data = null;
                            if (exitus == CTAExecutionResult.COMPLETED) {
                                data = this.statsConverter.ctaComplete(element.action);
                                window.setTimeout(() => this.triggerCTAComplete(), 0);
                            }
                            else if (exitus == CTAExecutionResult.DISMISSED) {
                                data = this.statsConverter.ctaDismissed(element.action);
                                window.setTimeout(() => this.triggerCTADismiss(), 0);
                            }
                            if (data != null) {
                                this.stats.collectCTAData( data );
                            }
                            return exitus;
                        }
                    );
                }
                else {
                    return Promise.reject("Unsupported action " + element.action.type);
                }
            }
        );
    }

    /**
     * Restituisce una promise che risolve a VALUTAZIONE completa 
     * @param name 
     * @param forced 
     * @param data 
     */
    evaluateNamedCTAQueueUpdate(name: EvaluationName, forced: boolean, data?: Record<string, any>): Promise<SpecificEvaluatedAction|null> {
        return this.evaluateNamedCTA(name, forced).then(
            act => {               
                this.enqueueAction(act, data);
                return act;
            }
        );
    }

    enqueueAction(action: SpecificEvaluatedAction, data?: Record<string, any>): void {
        if(action) {
            this.queue.push({
                action: action,
                data: data
            });
        }
        else {
            this.logger.debug("Not enqueuing null action");
        }

    }

    protected abstract evaluateNamedCTA(name: EvaluationName, forced: boolean): Promise<SpecificEvaluatedAction|null>;

    public execCTA(): Promise<void> {
        return this.evaluateCTA().then(
            act => {
                if(this.execOnce && this.executionCounter > 0) {
                    this.logger.warn("Execution counter reached", this.executionCounter, "and executor is marked for single execution");
                }
                else {
                    this.logger.debug("Start execCTA");
                    this.executionCounter++;
                    
                    this.enqueueAction(act);
                    return this.processQueue();
                }
            }
        ).then();
    }

    /**
     * Metodo per forzare l'esecuzione di un'azione.
     * @warn Questo metodo restituisce la promise relativa alla action pushata, non all'esecuzione completa della coda.
     * @param action 
     */
    public forceActionExecution(action: ManualAction): Promise<void> {
        this.enqueueAction(action as SpecificEvaluatedAction);
        return this.processQueue().then();
    }
}